/**
 * Export Formats — SPA adapter.
 *
 * Delegates text-based exports to @chromis/shared-logic's exportPalette().
 * Browser-specific methods (PNG via Canvas, PDF, downloadFile) stay here.
 */

import { exportPalette } from '@chromis/shared-logic';
import { PaletteRecord } from './database';

export class ExportFormats {
  // ── Text-based exports → delegate to shared-logic ──────────────────────

  private async delegate(palette: PaletteRecord, format: string): Promise<string> {
    const names = palette.colors.map((_, i) => `color-${i + 1}`);
    const result = await exportPalette(palette.colors, names, format, { paletteName: palette.name });
    return result.content;
  }

  exportAsJSON(palette: PaletteRecord): string {
    return JSON.stringify(palette, null, 2);
  }

  async exportAsCSS(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'css'); }
  async exportAsSCSS(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'scss'); }
  async exportAsTailwindConfig(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'tailwind'); }
  async exportAsSwift(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'swift'); }
  async exportAsSwiftUI(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'swiftui'); }
  async exportAsAndroidXML(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'android'); }
  async exportAsGPL(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'gpl'); }
  async exportAsSVG(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'svg'); }
  async exportAsSwatches(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'swatches'); }
  async exportAsGradientCSS(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'gradient-css'); }
  async exportAsFigmaTokens(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'figma-tokens'); }
  async exportAsMaterialTokens(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'material-tokens'); }
  async exportAsRestPayload(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'rest-payload'); }
  async exportAsJetpackCompose(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'kotlin'); }
  async exportAsFlutter(palette: PaletteRecord): Promise<string> { return this.delegate(palette, 'flutter'); }

  // ── Binary text-based exports → delegate and extract binary ────────────

  async exportAsASE(palette: PaletteRecord): Promise<Blob> {
    const names = palette.colors.map((_, i) => `Color ${i + 1}`);
    const result = await exportPalette(palette.colors, names, 'ase', { paletteName: palette.name });
    if (result.binary) {
      return new Blob([new Uint8Array(result.binary)], { type: 'application/octet-stream' });
    }
    return new Blob([result.content], { type: 'application/octet-stream' });
  }

  async exportAsACT(palette: PaletteRecord): Promise<Blob> {
    const names = palette.colors.map((_, i) => `Color ${i + 1}`);
    const result = await exportPalette(palette.colors, names, 'act', { paletteName: palette.name });
    if (result.binary) {
      return new Blob([new Uint8Array(result.binary)], { type: 'application/octet-stream' });
    }
    return new Blob([result.content], { type: 'application/octet-stream' });
  }

  async exportAsCLR(palette: PaletteRecord): Promise<Blob> {
    const names = palette.colors.map((_, i) => `Color ${i + 1}`);
    const result = await exportPalette(palette.colors, names, 'clr', { paletteName: palette.name });
    return new Blob([result.content], { type: 'application/xml' });
  }

  // ── Browser-specific exports (Canvas/DOM required) ─────────────────────

  async exportAsPNG(palette: PaletteRecord, options: {
    swatchSize?: number;
    columns?: number;
    showLabels?: boolean;
    showHex?: boolean;
  } = {}): Promise<Blob> {
    const {
      swatchSize = 100,
      columns = 8,
      showLabels = true,
      showHex = true,
    } = options;

    const rows = Math.ceil(palette.colors.length / columns);
    const labelHeight = showLabels ? 30 : 0;
    const hexHeight = showHex ? 20 : 0;
    const cellHeight = swatchSize + labelHeight + hexHeight;
    const padding = 10;

    const canvas = document.createElement('canvas');
    canvas.width = columns * swatchSize + (columns + 1) * padding;
    canvas.height = rows * cellHeight + (rows + 1) * padding + 40;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#000000';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(palette.name, canvas.width / 2, 30);

    palette.colors.forEach((color, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = col * swatchSize + (col + 1) * padding;
      const y = row * cellHeight + (row + 1) * padding + 40;

      ctx.fillStyle = color;
      ctx.fillRect(x, y, swatchSize, swatchSize);

      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, swatchSize, swatchSize);

      if (showLabels) {
        ctx.fillStyle = '#000000';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`Color ${index + 1}`, x + swatchSize / 2, y + swatchSize + 18);
      }

      if (showHex) {
        ctx.fillStyle = '#666666';
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(color, x + swatchSize / 2, y + swatchSize + labelHeight + 15);
      }
    });

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob || new Blob());
      }, 'image/png');
    });
  }

  async exportAsPDF(palette: PaletteRecord): Promise<Blob> {
    const title = palette.name;
    const pdf = `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /Resources 4 0 R /MediaBox [0 0 612 792] /Contents 5 0 R >>
endobj
4 0 obj
<< /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> >> >>
endobj
5 0 obj
<< /Length 100 >>
stream
BT
/F1 24 Tf
50 750 Td
(${title}) Tj
ET
endstream
endobj
xref
0 6
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000230 00000 n
0000000329 00000 n
trailer
<< /Size 6 /Root 1 0 R >>
startxref
479
%%EOF`;
    return new Blob([pdf], { type: 'application/pdf' });
  }

  // ── Browser download helper ────────────────────────────────────────────

  downloadFile(content: string | Blob, filename: string, mimeType: string = 'text/plain') {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const exportFormats = new ExportFormats();
